import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import type { DocumentSubjectsResponse } from 'features/knowledgeDocumentViewer/types/documentSubjects'
import { useGeckoPermissions } from 'features/permissions'
import useSWR from 'swr'
import { PERMISSION_GECKO_INTERNAL_MEMBER } from 'types'

/**
 * All fused subjects grounded on a document — asset kinds AND doc references
 * in one request (`include_doc_references=true`; the consumer splits by
 * `kind`). Deliberately no `page` param: the server read is O(site assets)
 * regardless of page, boxes carry their page, and every overlay already
 * filters client-side — so one fetch serves the whole document and page
 * turns are free (stable SWR key per document).
 *
 * Fim subjects are gecko-internal-only: for external users the fetch is
 * skipped here at the source, so every subject surface downstream (alias and
 * doc-reference overlays, tooltips, the subject sidebar panel) renders empty.
 */
export function useDocumentSubjects(
  site: string | undefined,
  documentId: string | undefined
) {
  const { authorizedAxios: axios, isAuthenticated } = useGeckoAuth()
  const { hasPermissions } = useGeckoPermissions()
  const { FIM_SERVICE_URL } = useRuntimeVariables()
  const isGeckoUser = hasPermissions([PERMISSION_GECKO_INTERNAL_MEMBER])

  const endpoint =
    site && documentId
      ? `${FIM_SERVICE_URL}/api/v1/sites/${encodeURIComponent(
          site
        )}/documents/${encodeURIComponent(
          documentId
        )}/subjects?include_doc_references=true`
      : null

  const { data, error, isLoading } = useSWR<DocumentSubjectsResponse, Error>(
    isAuthenticated && isGeckoUser && site && documentId ? endpoint : null,
    async (url: string) => {
      const res = await axios.get(url, {
        headers: { 'Content-Type': 'application/json' },
      })
      return res.data
    }
  )

  return { data, error, isLoading }
}
